/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialRegulation } from './SpecialRegulation';
import {
    SpecialRegulationFromJSON,
    SpecialRegulationFromJSONTyped,
    SpecialRegulationToJSON,
    SpecialRegulationToJSONTyped,
} from './SpecialRegulation';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Dimensions } from './Dimensions';
import {
    DimensionsFromJSON,
    DimensionsFromJSONTyped,
    DimensionsToJSON,
    DimensionsToJSONTyped,
} from './Dimensions';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * A piece is the smallest logistics unit containing product items that cannot be dismantled during the logistics chain. A piece can contain other pieces, but the piece at the top of the hierarchy remains constant throughout the chain. Pieces can be pallets, parcels, or even containers that are not seen as reusable objects in the logistics chain.
 *       A piece has a unique identifier to ensure traceability of the physical object. Providers using their own labeling system can register their label ID in an external reference on the piece.
 *       Bulk products should be identified as a BatchOfGoods because there is no packaging to identify them.
 * @export
 * @interface Piece
 */
export interface Piece {
    /**
     * Free text to describe the goods contained in the piece when the product items data are not provided.
     * @type {string}
     * @memberof Piece
     */
    goodsDescription?: string;
    /**
     * Indicates how many pieces can be put on top of the piece.
     * @type {number}
     * @memberof Piece
     */
    stackingMaxNumber?: number;
    /**
     * Indicates whether the piece can be rotated freely or must remain in a fixed position during handling and transport.
     * @type {boolean}
     * @memberof Piece
     */
    turnable?: boolean;
    /**
     * The container type of the piece. List of possible values is defined in another ontology.
     * @type {string}
     * @memberof Piece
     */
    containerType?: string;
    /**
     * The container subtype of the piece. List of possible values is defined in another ontology.
     * @type {string}
     * @memberof Piece
     */
    containerSubtype?: string;
    /**
     * List of contacts related to the piece. These can be persons or teams to be contacted regarding the piece. This list may be filled out if a contact is required for a particular piece.
     * @type {Array<Contact>}
     * @memberof Piece
     */
    contacts?: Array<Contact>;
    /**
     * Product items placed inside a piece for the entire lifecycle of the logistics chain.
     * @type {Array<Link>}
     * @memberof Piece
     */
    containedItems?: Array<Link>;
    /**
     * Pieces can be nested within other pieces. Pieces and their contained pieces remain together during the full life cycle of the logistics chain. However, if there comes a point in the logistics chain where some pieces must be separated from each other, they should not be linked by the concept of 'piece in piece'. Instead, they should be placed in the same resource container during the processes in which they are managed together.
     * @type {Array<Link>}
     * @memberof Piece
     */
    containedPieces?: Array<Link>;
    /**
     * Dimensions of the piece, including height, length, and width. If any of these measurements cannot be obtained, only the volume will be provided.
     * @type {Dimensions}
     * @memberof Piece
     */
    dimensions?: Dimensions;
    /**
     * List of documents related to the piece.
     * @type {Array<Document>}
     * @memberof Piece
     */
    documents?: Array<Document>;
    /**
     * External references of the piece should be used when they are defined in a legacy system registering the piece under another reference identifier than the URI. This is useful to search piece using the Id from the legacy app. Additionally, a provider can register his label number in case of labeling.
     *                          Frequent ExternalReference Types:
     *                          - 'SELLER_ID'
     *                          - 'BUYER_ID'
     *                          - 'PROVIDER_ID'
     *                          - '[provider_name]_LABEL_ID' 
     * @type {Array<ExternalReference>}
     * @memberof Piece
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Weight of the piece including the weight of the package or support (including the weight of the cardboard for a parcel, weight of the pallet...).
     * @type {Value}
     * @memberof Piece
     */
    grossWeight?: Value;
    /**
     * 
     * @type {Link}
     * @memberof Piece
     */
    isInPiece?: Link;
    /**
     * Sum of the weights of the items contained in the piece excluding the weight of the piece package.
     * @type {Value}
     * @memberof Piece
     */
    netWeight?: Value;
    /**
     * Special characteristics applied to the piece and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Piece
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the piece and to be shared with a provider, such as handling instructions, protocols to be applied, or any other specific requirements.
     * @type {Array<SpecialInstruction>}
     * @memberof Piece
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Special regulations applied to the piece.
     * @type {Array<SpecialRegulation>}
     * @memberof Piece
     */
    specialRegulations?: Array<SpecialRegulation>;
}

/**
 * Check if a given object implements the Piece interface.
 */
export function instanceOfPiece(value: object): value is Piece {
    return true;
}

export function PieceFromJSON(json: any): Piece {
    return PieceFromJSONTyped(json, false);
}

export function PieceFromJSONTyped(json: any, ignoreDiscriminator: boolean): Piece {
    if (json == null) {
        return json;
    }
    return {
        
        'goodsDescription': json['goodsDescription'] == null ? undefined : json['goodsDescription'],
        'stackingMaxNumber': json['stackingMaxNumber'] == null ? undefined : json['stackingMaxNumber'],
        'turnable': json['turnable'] == null ? undefined : json['turnable'],
        'containerType': json['containerType'] == null ? undefined : json['containerType'],
        'containerSubtype': json['containerSubtype'] == null ? undefined : json['containerSubtype'],
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'containedItems': json['containedItems'] == null ? undefined : ((json['containedItems'] as Array<any>).map(LinkFromJSON)),
        'containedPieces': json['containedPieces'] == null ? undefined : ((json['containedPieces'] as Array<any>).map(LinkFromJSON)),
        'dimensions': json['dimensions'] == null ? undefined : DimensionsFromJSON(json['dimensions']),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'grossWeight': json['grossWeight'] == null ? undefined : ValueFromJSON(json['grossWeight']),
        'isInPiece': json['isInPiece'] == null ? undefined : LinkFromJSON(json['isInPiece']),
        'netWeight': json['netWeight'] == null ? undefined : ValueFromJSON(json['netWeight']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'specialRegulations': json['specialRegulations'] == null ? undefined : ((json['specialRegulations'] as Array<any>).map(SpecialRegulationFromJSON)),
    };
}

export function PieceToJSON(json: any): Piece {
    return PieceToJSONTyped(json, false);
}

export function PieceToJSONTyped(value?: Piece | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'goodsDescription': value['goodsDescription'],
        'stackingMaxNumber': value['stackingMaxNumber'],
        'turnable': value['turnable'],
        'containerType': value['containerType'],
        'containerSubtype': value['containerSubtype'],
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'containedItems': value['containedItems'] == null ? undefined : ((value['containedItems'] as Array<any>).map(LinkToJSON)),
        'containedPieces': value['containedPieces'] == null ? undefined : ((value['containedPieces'] as Array<any>).map(LinkToJSON)),
        'dimensions': DimensionsToJSON(value['dimensions']),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'grossWeight': ValueToJSON(value['grossWeight']),
        'isInPiece': LinkToJSON(value['isInPiece']),
        'netWeight': ValueToJSON(value['netWeight']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'specialRegulations': value['specialRegulations'] == null ? undefined : ((value['specialRegulations'] as Array<any>).map(SpecialRegulationToJSON)),
    };
}

